import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle, Circle, Loader2, Scale, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LegalProgressBar = ({ dealId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [updating, setUpdating] = useState(null);

  const fetchProgress = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/deals/${dealId}/legal-progress`);
      setData(res.data);
    } catch (err) {
      logger.error('Failed to load legal progress:', err);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    if (dealId) fetchProgress();
  }, [dealId, fetchProgress]);

  const toggleMilestone = async (key, currentState) => {
    setUpdating(key);
    try {
      await axios.put(`${API_URL}/api/deals/${dealId}/legal-milestone`, {
        milestone_key: key,
        completed: !currentState,
      });
      toast.success(`Legal step ${!currentState ? 'completed' : 'unchecked'}`);
      fetchProgress();
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setUpdating(null);
    }
  };

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-[#E07A3A]" /></div>;
  if (!data) return null;

  const { milestones, completed, total, progress_pct, country, listing_type, verified_offer, verified_offer_type } = data;

  const isRental = listing_type === 'rent';
  const countryLabelMap = isRental ? {
    UK: 'UK Tenancy Process',
    India: 'India Rental Agreement',
    Canada: 'Canadian Lease Process',
  } : {
    UK: 'England & Wales Conveyancing',
    India: 'RERA Compliance Process',
    Canada: 'Canadian Closing Process',
  };

  return (
    <div className="luxury-card overflow-hidden mt-4" data-testid="legal-progress-bar">
      <div className="p-4 cursor-pointer flex items-center justify-between" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          <Scale className="h-5 w-5 text-[#E07A3A]" />
          <div>
            <h4 className="text-sm font-semibold text-[#1A1A1A]">Legal Progress</h4>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">{countryLabelMap[country] || country}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {verified_offer && (
            <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="verified-offer-badge">
              Verified {verified_offer_type || 'Offer'}
            </span>
          )}
          <span className="text-lg font-semibold text-[#E07A3A]">{progress_pct}%</span>
          <span className="text-[10px] text-slate-400">{completed}/{total}</span>
          {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-4 pb-2">
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${progress_pct === 100 ? 'bg-emerald-500' : 'bg-[#E07A3A]'}`}
            style={{ width: `${progress_pct}%` }}
          />
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 p-4 space-y-0">
          {milestones.map((ms, i) => (
            <div key={ms.key} className="flex items-start gap-3 py-2">
              {/* Timeline line */}
              <div className="flex flex-col items-center">
                <button
                  onClick={() => toggleMilestone(ms.key, ms.completed)}
                  disabled={updating === ms.key}
                  className="focus:outline-none"
                  data-testid={`legal-ms-${ms.key}`}
                >
                  {updating === ms.key ? (
                    <Loader2 className="h-5 w-5 animate-spin text-[#E07A3A]" />
                  ) : ms.completed ? (
                    <CheckCircle className="h-5 w-5 text-emerald-500" />
                  ) : (
                    <Circle className="h-5 w-5 text-slate-300 hover:text-[#E07A3A] transition-colors" />
                  )}
                </button>
                {i < milestones.length - 1 && (
                  <div className={`w-0.5 h-6 ${ms.completed ? 'bg-emerald-300' : 'bg-slate-200'}`} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${ms.completed ? 'text-emerald-700 font-medium' : 'text-slate-600'}`}>{ms.label}</p>
                {ms.completed_at && (
                  <p className="text-[10px] text-slate-400">
                    {new Date(ms.completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LegalProgressBar;
